import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { BRAND, ETSY_SHOP_URL, GROUPS, SECTIONS } from "@/lib/site";
import { useSeason } from "@/hooks/useSeason";
import { usePosts } from "@/hooks/usePosts";
import { gaEvent, loadGA, track } from "@/lib/track";

const GROUP_ORDER = ["who", "when", "how", "kids"];

const NAV = [
  { to: "ideas", label: "Gift Ideas", id: "ideas" },
  { to: "/gifts/kids", label: "Little Hands", id: "kids" },
  { to: "/gift-finder", label: "Gift Finder", id: "gift-finder" },
  { to: "/about", label: "About", id: "about" },
  { to: "/shop", label: "Shop", id: "shop" },
];
const MORE = [
  { to: "/blog", label: "All gift ideas" }, { to: "/gift-finder", label: "Gift Finder" }, { to: "/guides", label: "Gift guides" }, { to: "/gallery", label: "Reader gallery" },
  { to: "/free-printable", label: "Free printable" }, { to: "/about", label: "About Kelly" },
];
const navCls = (on: boolean) => `shop-nav-link px-2.5 py-2 ${on ? "text-[#1C1917] underline decoration-[#B85C38] decoration-2 underline-offset-[10px]" : "text-[#57534E] hover:text-[#1C1917]"}`;

function GiftIdeas() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const on = pathname === "/blog" || (pathname.startsWith("/gifts/") && pathname !== "/gifts/kids");
  const posts = usePosts();
  const live = (slug: string) => !posts || posts.some((p) => p.section === slug);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) { return; }
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button className={`${navCls(on)} inline-flex items-center gap-1`} onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="site-nav-ideas">
        Gift Ideas <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="fixed inset-x-3 z-50 mt-1 rounded-xl border border-[#E7E5E4] bg-white p-4 shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:w-[640px]" data-testid="ideas-menu">
          <div className="grid gap-4 sm:grid-cols-3">
            {GROUPS.map((g) => (
              <div key={g.key}>
                <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#78716C]">{g.label}</p>
                {SECTIONS.filter((s) => s.group === g.key && live(s.slug)).map((s) => (
                  <Link key={s.slug} to={`/gifts/${s.slug}`} className="block rounded-lg px-3 py-1.5 text-sm font-medium text-[#1C1917] hover:bg-[#F5F5F4]" data-testid={`ideas-menu-${s.slug}`}>{s.name}</Link>
                ))}
              </div>
            ))}
          </div>
          <Link to="/blog" className="mt-3 flex items-center justify-between rounded-lg bg-[#FAFAF9] px-3 py-2 text-sm font-semibold text-[#1C1917] hover:bg-[#F5F5F4]" data-testid="ideas-menu-all">See all gift ideas <span aria-hidden>→</span></Link>
        </div>
      )}
    </div>
  );
}

function SeasonLogo({ className }: { className: string }) {
  return <img src="/logo/everyday.png" alt={`${BRAND} logo`} className={className} data-testid="site-logo-mark" />;
}

function SeasonStrip() {
  const s = useSeason();
  if (!s) return <div className="h-8 border-b border-[#F5F5F4] bg-[#FAFAF9]" />;
  return (
    <Link to={`/${s.preview ? `?season=${s.key}` : ""}#right-now`} className="flex h-8 items-center justify-center gap-2 overflow-hidden border-b border-[#F5F5F4] bg-[#FAFAF9] px-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#78716C] transition-colors hover:text-[#1C1917]" data-testid="season-strip">
      <span className="truncate">{s.key === "everyday" ? "Fresh handmade gift ideas every week" : `In season: ${s.name}`}</span>
      <span className="hidden underline underline-offset-2 sm:inline">See them</span>
      {s.preview && <span className="rounded-full bg-white px-2 py-0.5 text-[10px] text-[#23324A]">PREVIEW</span>}
    </Link>
  );
}

export default function SiteLayout() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => { loadGA(); }, []);
  useEffect(() => { track("page_view", pathname); gaEvent("page_view", { page_path: pathname }); }, [pathname]);
  const posts = usePosts();

  return (
    <div className="site-shop flex min-h-screen flex-col" data-testid="site-layout">
      <SeasonStrip />
      <header className="sticky top-0 z-40 border-b border-[#E7E5E4] bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-6 gap-y-2 px-5 py-3.5 sm:px-8">
          <Link to="/" className="flex items-center gap-2.5 font-['Plus_Jakarta_Sans'] text-xl font-bold tracking-tight text-[#1C1917]" data-testid="site-logo"><SeasonLogo className="h-9 w-9" />{BRAND}</Link>
          <nav className="-mx-2 flex w-full gap-1 overflow-x-auto whitespace-nowrap text-sm sm:mx-0 sm:w-auto sm:overflow-visible">
            {NAV.map((n) => n.to === "ideas" ? <GiftIdeas key={n.id} /> : (
              <NavLink key={n.to} to={n.to} data-testid={`site-nav-${n.id}`} className={({ isActive }) => `${navCls(isActive)} ${n.id === "about" ? "hidden sm:inline" : ""}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="flex-1"><Outlet /></main>
      <footer className="mt-32 overflow-hidden bg-[#0C0A09] text-[#A8A29E]">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-10 pt-16 sm:grid-cols-[1.3fr_1fr_1fr_1fr] sm:px-8">
          <div>
            <p className="flex items-center gap-3 font-['Plus_Jakarta_Sans'] text-xl font-bold tracking-tight text-white" data-testid="footer-brand"><span className="grid h-11 w-11 place-items-center rounded-xl bg-white"><img src="/logo/everyday.png" alt="" className="h-8 w-8" /></span>{BRAND}</p>
            <p className="mt-2 max-w-xs text-sm text-white/70">Handmade gift ideas with real instructions, and printable kits for everyone on your list.</p>
          </div>
          <div className="text-sm">
            <p className="mb-3 font-semibold">Gift ideas</p>
            {[...SECTIONS].sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group)).filter((s) => !posts || posts.some((p) => p.section === s.slug)).map((s) => <Link key={s.slug} to={`/gifts/${s.slug}`} className="block py-1 text-white/75 hover:text-white" data-testid={`footer-section-${s.slug}`}>{s.name}</Link>)}
          </div>
          <div className="text-sm">
            <p className="mb-3 font-semibold">More</p>
            {MORE.map((m) => <Link key={m.to} to={m.to} className="block py-1 text-white/75 hover:text-white" data-testid={`footer-more-${m.to.slice(1)}`}>{m.label}</Link>)}
          </div>
          <div className="text-sm">
            <p className="mb-3 font-semibold">Shop</p>
            <Link to="/shop" className="block py-1 text-white/75 hover:text-white">All printables</Link>
            <a href={ETSY_SHOP_URL} target="_blank" rel="noreferrer" className="block py-1 text-white/75 underline hover:text-white" data-testid="footer-etsy-link">Visit our Etsy shop</a>
            <p className="mt-6 text-xs text-white/55">© {new Date().getFullYear()} {BRAND} · Printables for personal use</p>
          </div>
        </div>
        <p className="pointer-events-none select-none whitespace-nowrap px-5 pb-6 text-center font-['Plus_Jakarta_Sans'] text-[13vw] font-bold leading-none tracking-tighter text-[#292524] sm:text-[11vw] xl:text-[150px]" aria-hidden>{BRAND}</p>
      </footer>
    </div>
  );
}
